import { Fragment, useMemo, type ReactNode } from "react";
import { cibOutline, parseCibInline, parseCibMarkdown, type CibBlock } from "../brief-document";

function InlineText({ value }: { value: string }): JSX.Element {
  return (
    <>
      {parseCibInline(value).map((token, index): ReactNode => {
        const key = `${token.kind}-${index}`;
        switch (token.kind) {
          case "strong":
            return <strong key={key}>{token.text}</strong>;
          case "emphasis":
            return <em key={key}>{token.text}</em>;
          case "code":
            return <code key={key}>{token.text}</code>;
          case "link":
            return (
              <a key={key} href={token.href} target="_blank" rel="noreferrer noopener">
                {token.text}
              </a>
            );
          case "text":
            return <Fragment key={key}>{token.text}</Fragment>;
        }
      })}
    </>
  );
}

function Heading({ block }: { block: Extract<CibBlock, { kind: "heading" }> }) {
  const contents = <InlineText value={block.text} />;
  if (block.level === 1) return <h1 id={block.id}>{contents}</h1>;
  if (block.level === 2) return <h2 id={block.id}>{contents}</h2>;
  if (block.level === 3) return <h3 id={block.id}>{contents}</h3>;
  return <h4 id={block.id}>{contents}</h4>;
}

export function BriefDocument({ body }: { body: string }): JSX.Element {
  const blocks = useMemo(() => parseCibMarkdown(body), [body]);
  const outline = useMemo(() => cibOutline(blocks), [blocks]);

  return (
    <div className="sxco-cib-reading-layout">
      {outline.length >= 3 && (
        <nav className="sxco-cib-toc" aria-label="Brief contents">
          <p className="sxco-cib-toc-title">In this brief</p>
          <ol>
            {outline.slice(0, 14).map((item) => (
              <li key={item.id} className={item.level === 3 ? "sxco-cib-toc-subitem" : undefined}>
                <a href={`#${item.id}`}>{item.text}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      <article className="sxco-cib-prose">
        {blocks.map((block, index) => {
          const key = `${block.kind}-${index}`;
          switch (block.kind) {
            case "heading":
              return <Heading key={key} block={block} />;
            case "paragraph":
              return (
                <p key={key}>
                  <InlineText value={block.text} />
                </p>
              );
            case "list": {
              const List = block.ordered ? "ol" : "ul";
              return (
                <List key={key}>
                  {block.items.map((item, itemIndex) => (
                    <li key={`${key}-${itemIndex}`}>
                      <InlineText value={item} />
                    </li>
                  ))}
                </List>
              );
            }
            case "table":
              return (
                <div
                  key={key}
                  className="sxco-cib-table-wrap"
                  role="region"
                  aria-label="Brief data table"
                  tabIndex={0}
                >
                  <table>
                    <thead>
                      <tr>
                        {block.headers.map((header, cellIndex) => (
                          <th key={`${key}-head-${cellIndex}`} scope="col">
                            <InlineText value={header} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, rowIndex) => (
                        <tr key={`${key}-row-${rowIndex}`}>
                          {row.map((cell, cellIndex) => (
                            <td key={`${key}-cell-${rowIndex}-${cellIndex}`}>
                              <InlineText value={cell} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            case "quote":
              return (
                <blockquote key={key}>
                  <InlineText value={block.text} />
                </blockquote>
              );
            case "divider":
              return <hr key={key} />;
          }
        })}
      </article>
    </div>
  );
}
